import { pollingEventStatus, EventType } from '@/common/polling';
import { upgradeSuccessPage, wiseUpgradeSuccessPage } from '@/common/service/common';
import store from '@/store/index';
import { pageConfig } from '@/common/service/page-config';
import { planReactiveRequest, updatePlans } from '@/apis';
import { cloneDeep } from 'lodash';
import { omitBy } from 'lodash';
import { APM, WISE } from '@/views/checkout/payment/helper/constant';
import { getOrderSummaryFields } from '@/flows/utils';
import { afterPurchaseSuccessHandlerInModalCheckout } from '@/utils/modal-utils.js';

export const UpdateTypes = {
  UPDATE_BASE: 1, // Update Zoom Meeting / BIZ Plus
  UPDATE_ADDON: 2, // Update other plans.
  UPDATE_PBX: 3,
  PRO_TO_BIZ: 4,
  MEETING_TO_BIZ_PLUS: 5, // upgrade pro/biz to biz plus
  PRO_TO_BIZ_PLUS: 5, // upgrade pro to biz plus
  BIZ_TO_BIZ_PLUS: 5, // upgrade biz to biz plus
  PRO_PLUS_TO_PRO_PLUS: 5, // upgrade pro plus to pro plus
  BIZ_TO_PRO: 6,
  BIZ_PLUS_TO_BIZ: 7, // downgrade biz plus to biz & biz plus to pro plus
  DOWNGRADE_TO_ZONE_BASIC_PLUS: 8,
  ZONE_BASIC_PLUS_UPGRADE_TO_MEETING: 9,
  UPDATE_TOLL_FREE: 10,
  BIZ_PLUS: 11,
  MIX_BUY_PARTNER: 12, // JUST FOR MIX BUY CASE (edit base && add addon mix case) mix buy partnership plan
  PRO_TO_PTO_PLUS: 13, // pro upgrade to pro plus & biz to pro plus
  UPDATE_PRO_PLUS: 14, // edit pro plus
  ZS_TO_ZE: 15, // zs to ze
  SWITCH: 16, // switch plan (for example: ZE SaaS -> ZE PPA / ZS SaaS -> ZS PPA / w500 -> w100)
  EDU_SCH_CMP_PLUS_UPDATE: 18, // update edu school plus
  EDU_SCH_CMP_PLUS_DOWNGRADE: 17, // edu school plus => edu school
  EDU_SCH_CMP_UPGRADE: 19, // edu school => edu school plus
  ADD_TO_CART_UPDATE: 20, // edit base meeting plan in add-to-cart flow
  BIZ_TO_PRO_PLUS: 201,
  BIZ_PLUS_TO_PRO_PLUS: 211,
  EDU_ANNUAL_TO_SCH_CMP: 21,
};

export const removeEmptyValue = (obj) => omitBy(obj, (v) => v === '' || v === undefined || v === null);

export const getPaymentInfo = (getters) => {
  if (pageConfig.hasBillingZuoraId) {
    return {};
  }

  const apmInfo = getters.apmInfo;
  let { paymentResponse, paymentGateway } = getters.paymentMethodInfo;

  if (apmInfo.isApm) {
    paymentResponse = JSON.stringify({
      integrationType: APM,
    });
  }

  return {
    contact: cloneDeep([removeEmptyValue(getters.billToContact), removeEmptyValue(getters.soldToContact)]),
    paymentInfo: { paymentResponse, paymentGateway },
  };
};

export const updatePlansCheckout = async (
  submitParams,
  planList,
  actionType,
  couponData,
  extensionUpdateData,
  multiRatePlan = false,
) => {
  const { paymentResponse } = store.getters.paymentMethodInfo;
  const preview = submitParams?.preview ?? false;
  const previewActions = submitParams?.previewActions ?? [];
  const previewType = store.getters.getPreviewType;

  const { apmInfo } = store.getters;
  const params = {
    actionType,
    planList,
    paymentResponse: paymentResponse,
    couponData,
    extensionUpdateData,
    multiRatePlan,
    preview,
    previewActions,
    orderSummary: {
      orderSummaryFields: getOrderSummaryFields(store.getters),
    },
  };
  if (previewType) {
    params.previewType = previewType;
  }
  addParamsIfApm(params);
  const result = await updatePlans(params);
  const options = preview ? { previewKey: result?.result?.previewKey, rps: true } : { rps: true };
  const eventResult = await pollingEventStatus(options);
  if (!preview) {
    if (window?.$context?.is?.modal) {
      afterPurchaseSuccessHandlerInModalCheckout();
    }

    !pageConfig?.skipSuccessPage && // * if skipSuccessPage = true, not navigate to successpage, for example in modal checkout add license edit flow.
      (apmInfo.apmPaymentMethodName === WISE ?
        wiseUpgradeSuccessPage({ actionType }) :
        upgradeSuccessPage(null, actionType)
      );
  }

  return { eventResult };
};

export const addParamsIfApm = (params) => {
  if (pageConfig?.apmP2PEnabled && store.getters.apmInfo?.isApm) {
    params.apmData = {
      apmName: store.getters.apmInfo?.apmPaymentMethodName,
    };
    if (store.getters.apmInfo?.apmPaymentMethodName === WISE) {
      params.apmData.wisePaymentName = store.getters.apmInfo?.tileImageFileName;
    }
  }
};

export const reactivatePlan = async (meetingPlan) => {
  const postData = {
    plan_code: meetingPlan.plan_code,
    planType: meetingPlan.type,
  };
  let resp;
  try {
    resp = await planReactiveRequest(postData);
  } catch (e) {
    throw typeof e === 'string' ? e : e?.message;
  }
  if (resp?.result?.status === 1) {
    await pollingEventStatus({ eventType: EventType.BasePLanReactivateEvent });
    return true;
  }
};

// Postoned to Dec release
// export const reactivateSubPlan = async (meetingPlan) => {
//   const postData = {
//     plan_code: meetingPlan.plan_code,
//     planType: meetingPlan.type,
//   };
//   let resp;
//   try {
//     resp = await planReactiveSubPlanRequest(postData);
//   } catch (e) {
//     throw typeof e === 'string' ? e : e?.message;
//   }
//   if (resp?.result?.status === 1) {
//     await pollingEventStatus({ eventType: EventType.AddonPlanReactivateEvent });
//     return true;
//   }
// }
